import React, { useState } from "react";
import { ChevronDown, ChevronRight, Trash2, Split } from "lucide-react";
import RowReorderControls from "components/Workflows/RowReorderControls";
import DependsOnMultiSelect from "components/Workflows/DependsOnMultiSelect";
import VisibleWhenEditor from "components/Workflows/VisibleWhenEditor";
import { TICKET_PRIORITY_OPTIONS, TICKET_PRIORITY_LABELS } from "constants/tickets";
import { TICKET_KINDS } from "constants/ticketTemplates";

/**
 * FlowStepRow (SOUND-506) — one child ticket the flow will generate. Inline:
 * key (dependency handle) / title / assigneeRole (a function queue) / priority /
 * kind, an expander for the optional description, the earlier-only
 * DependsOnMultiSelect, reorder, and delete. `key` auto-slugs from the title.
 *
 * SOUND-546 — an optional `spawnWhen` condition ("Only create this step
 * when…"), authored with the intake field's VisibleWhenEditor. `intakeFields`
 * are ALL of this template's intake fields (editor model, `_id`-keyed): a step
 * runs after intake, so there is no earlier-only rule across that boundary.
 */
export default function FlowStepRow({
  step,
  index,
  total,
  earlierSteps = [],
  intakeFields = [],
  functions = [],
  onChange,
  onMove,
  onDelete,
  keyError,
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-start gap-2 p-2.5">
        <RowReorderControls
          onUp={() => onMove(-1)}
          onDown={() => onMove(1)}
          isFirst={index === 0}
          isLast={index === total - 1}
        />
        <span className="mt-1.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand/15 text-xs font-semibold text-brand">
          {index + 1}
        </span>

        <div className="min-w-0 flex-1 space-y-2">
          <input
            className="input h-9 w-full text-sm font-medium"
            placeholder="Step title (becomes the child ticket title)"
            value={step.title || ""}
            onChange={(e) => onChange({ title: e.target.value })}
          />
          {(step.key || step.spawnWhen) && (
            <div className="flex flex-wrap items-center gap-2">
              {step.key && <span className="font-mono text-[11px] text-muted-foreground">key: {step.key}</span>}
              {step.spawnWhen && (
                <span className="badge badge-info inline-flex items-center gap-1">
                  <Split className="h-3 w-3" />
                  Conditional
                </span>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="select h-8 w-auto text-xs"
              value={step.assigneeRole || ""}
              onChange={(e) => onChange({ assigneeRole: e.target.value })}
              title="Assignee function (queue)"
            >
              <option value="">Unassigned queue</option>
              {functions.map((f) => (
                <option key={f.functionKey} value={f.functionKey}>
                  {f.label}
                </option>
              ))}
            </select>
            <select
              className="select h-8 w-auto text-xs"
              value={step.defaultPriority || "normal"}
              onChange={(e) => onChange({ defaultPriority: e.target.value })}
              title="Default priority"
            >
              {TICKET_PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {TICKET_PRIORITY_LABELS[p]}
                </option>
              ))}
            </select>
            <div className="inline-flex rounded-md border border-border p-0.5">
              {TICKET_KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => onChange({ kind: k })}
                  className={`rounded px-2 py-0.5 text-xs font-semibold capitalize transition-colors ${
                    (step.kind || "task") === k
                      ? "bg-brand/15 text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Depends on:</span>
            <DependsOnMultiSelect
              earlierSteps={earlierSteps}
              selectedIds={step.dependsOn || []}
              onChange={(ids) => onChange({ dependsOn: ids })}
            />
          </div>
          <VisibleWhenEditor
            earlierFields={intakeFields}
            value={step.spawnWhen || null}
            onChange={(sw) => onChange({ spawnWhen: sw })}
            toggleLabel="Only create this step when…"
            leadText="Create only when"
            emptyHint="(needs an intake field)"
          />

          {open && (
            <textarea
              className="textarea min-h-[64px] w-full text-sm"
              placeholder="Description (optional) — shown on the generated child ticket"
              value={step.description || ""}
              onChange={(e) => onChange({ description: e.target.value })}
            />
          )}
        </div>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="btn btn-ghost h-8 w-8 shrink-0 p-0"
          aria-label="Step description"
          aria-expanded={open}
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="btn btn-ghost h-8 w-8 shrink-0 p-0 text-destructive"
          aria-label="Delete step"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {keyError && <div className="px-3 pb-2 text-xs text-destructive">{keyError}</div>}
    </div>
  );
}
